import { useState } from 'react';
import { Link } from 'react-router-dom';

type Role = 'client' | 'livreur';

interface WorkflowStep {
  icon: string;
  title: string;
  desc: string;
  tag?: string;
  tagColor?: string;
}

const WORKFLOWS: Record<Role, { color: string; bg: string; steps: WorkflowStep[] }> = {
  client: {
    color: 'teal',
    bg: 'from-teal-50 to-teal-100',
    steps: [
      {
        icon: 'ri-user-add-line',
        title: 'Créer un compte',
        desc: 'Inscription rapide avec email ou téléphone. Connexion possible sans email pour les zones rurales.',
        tag: 'Gratuit',
        tagColor: 'bg-teal-100 text-teal-700',
      },
      {
        icon: 'ri-search-line',
        title: 'Trouver un livreur',
        desc: 'Recherchez un livreur communautaire (quartier) ou professionnel (certifié) selon vos besoins.',
        tag: 'En temps réel',
        tagColor: 'bg-blue-100 text-blue-700',
      },
      {
        icon: 'ri-send-plane-line',
        title: 'Faire une demande',
        desc: 'Décrivez votre colis, l\'adresse de livraison. Attendez la validation du livreur.',
        tag: 'Validation < 5 min',
        tagColor: 'bg-orange-100 text-orange-700',
      },
      {
        icon: 'ri-map-pin-time-line',
        title: 'Suivre en temps réel',
        desc: 'Localisez votre colis en direct. Notifications à chaque étape de la livraison.',
        tag: 'GPS précis',
        tagColor: 'bg-purple-100 text-purple-700',
      },
      {
        icon: 'ri-check-double-line',
        title: 'Confirmer & Payer',
        desc: 'Confirmez la livraison et payez le livreur via Mobile Money — avant ou après livraison.',
        tag: 'Mobile Money',
        tagColor: 'bg-green-100 text-green-700',
      },
    ],
  },
  livreur: {
    color: 'orange',
    bg: 'from-orange-50 to-orange-100',
    steps: [
      {
        icon: 'ri-community-line',
        title: 'Inscription communautaire',
        desc: 'Sans formalités complexes. Juste votre téléphone et une pièce d\'identité. Validé en 24h.',
        tag: 'Simple & rapide',
        tagColor: 'bg-green-100 text-green-700',
      },
      {
        icon: 'ri-briefcase-line',
        title: 'Ou inscription professionnelle',
        desc: 'Avec formalités complètes (permis, assurance, véhicule). Validé par l\'admin. Missions prioritaires.',
        tag: 'Revenus élevés',
        tagColor: 'bg-blue-100 text-blue-700',
      },
      {
        icon: 'ri-notification-3-line',
        title: 'Recevoir des commandes',
        desc: 'Consultez les commandes validées dans votre zone. Acceptez ou refusez avec motif.',
        tag: 'En temps réel',
        tagColor: 'bg-orange-100 text-orange-700',
      },
      {
        icon: 'ri-route-line',
        title: 'Gérer les livraisons',
        desc: 'Suivez vos commandes en cours. Mettez à jour les statuts à chaque étape.',
        tag: 'Dashboard dédié',
        tagColor: 'bg-purple-100 text-purple-700',
      },
      {
        icon: 'ri-close-circle-line',
        title: 'Annuler si nécessaire',
        desc: 'Annulez une commande en cours avec un motif. Le client est notifié automatiquement.',
        tag: 'Avec motif',
        tagColor: 'bg-red-100 text-red-700',
      },
    ],
  },
  // admin: {
  //   color: 'blue',
  //   bg: 'from-blue-50 to-blue-100',
  //   steps: [
  //     {
  //       icon: 'ri-shield-user-line',
  //       title: 'Gestion des sous-admins',
  //       desc: 'Créez et gérez des administrateurs secondaires avec des permissions granulaires.',
  //       tag: 'RBAC',
  //       tagColor: 'bg-blue-100 text-blue-700',
  //     },
  //     {
  //       icon: 'ri-group-line',
  //       title: 'Gestion des clients',
  //       desc: 'Consultez, vérifiez et modérez les comptes clients. Gérez les statuts et zones.',
  //       tag: 'Filtres avancés',
  //       tagColor: 'bg-teal-100 text-teal-700',
  //     },
  //     {
  //       icon: 'ri-truck-line',
  //       title: 'Validation des livreurs',
  //       desc: 'Approuvez ou rejetez les inscriptions livreurs pros avec motif. Gérez les communautaires.',
  //       tag: 'Vérification docs',
  //       tagColor: 'bg-orange-100 text-orange-700',
  //     },
  //     {
  //       icon: 'ri-scales-3-line',
  //       title: 'Gestion des litiges',
  //       desc: 'Traitez les conflits entre clients et livreurs. Arbitrage et remboursements.',
  //       tag: 'Résolution < 48h',
  //       tagColor: 'bg-red-100 text-red-700',
  //     },
  //   ],
  // },
};

const ROLE_LABELS: Record<Role, { label: string; icon: string; cta: string; link: string }> = {
  client: { label: 'Je suis Client', icon: 'ri-user-line', cta: 'Créer mon compte client', link: '/register/client' },
  livreur: { label: 'Je suis Livreur', icon: 'ri-truck-line', cta: 'Devenir livreur', link: '/register/livreur' },
  // admin:   { label: 'Administration',  icon: 'ri-shield-user-line', cta: 'Accès administrateur',      link: '/login' },
};

export default function WorkflowSection() {
  const [activeRole, setActiveRole] = useState<Role>('client');
  const workflow = WORKFLOWS[activeRole];
  const roleLabel = ROLE_LABELS[activeRole];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Titre */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-teal-600 uppercase tracking-wider mb-2">
            Comment ça marche
          </p>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Un processus pensé pour chaque acteur
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Que vous soyez client ou livreur, votre parcours est simple et guidé.
          </p>
        </div>

        {/* Toggle rôles */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1">
            {(Object.keys(ROLE_LABELS) as Role[]).map(role => {
              const r = ROLE_LABELS[role];
              const isActive = role === activeRole;
              const colors: Record<Role, string> = {
                client: 'bg-teal-600 text-white shadow-teal-200',
                livreur: 'bg-orange-500 text-white shadow-orange-200',
                // admin:   'bg-blue-600 text-white shadow-blue-200',
              };
              return (
                <button
                  key={role}
                  onClick={() => setActiveRole(role)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
                    transition-all duration-200 ${isActive
                      ? `${colors[role]} shadow-lg`
                      : 'text-gray-500 hover:text-gray-800 hover:bg-white'
                    }`}
                >
                  <i className={r.icon} />
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Steps */}
        <div className={`bg-gradient-to-br ${workflow.bg} rounded-3xl p-8 md:p-10`}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflow.steps.map((step, idx) => (
              <div key={idx}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow relative">
                {/* Numéro d'étape */}
                <div className={`absolute -top-3 -left-3 w-7 h-7 rounded-full bg-${workflow.color}-600
                  text-white text-xs font-bold flex items-center justify-center shadow`}>
                  {idx + 1}
                </div>

                <div className={`w-12 h-12 bg-${workflow.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${step.icon} text-${workflow.color}-600 text-xl`} />
                </div>

                <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-3">{step.desc}</p>

                {step.tag && (
                  <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${step.tagColor}`}>
                    {step.tag}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* CTA du rôle */}
          <div className="text-center mt-8">
            <Link
              to={roleLabel.link}
              className={`inline-flex items-center gap-2 px-8 py-3.5 bg-${workflow.color}-600
                hover:bg-${workflow.color}-700 text-white font-semibold rounded-xl
                transition-colors shadow-lg`}
            >
              <i className={ROLE_LABELS[activeRole].icon} />
              {roleLabel.cta}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}